import { httpClient } from 'api/clients/httpClient'
import { CreateEventDto, EventDetails, EventType } from 'types/Event'
import { LocationType } from 'types/Location'
import { formatDate } from 'utils/event'

export const createSkeletonEvent = async (eventId: string, showId: string, partnerId?: string): Promise<void> => {
    try {
        const config = partnerId ? { params: { partnerId } } : {}
        await httpClient.post('/events/skeleton', { event_id: eventId, show_id: showId }, config)
    } catch (error) {
        console.error('Failed to create skeleton event:', error)
        throw new Error('Failed to create skeleton event')
    }
}

export const createEvent = async (eventData: CreateEventDto): Promise<void> => {
    try {
        await httpClient.post('/events', eventData)
    } catch (error) {
        console.error('Failed to create event:', error)
        throw new Error('Failed to create event')
    }
}

export const updateEvent = async (eventId: string, eventData: any, partnerId?: string): Promise<void> => {
    try {
        const config = partnerId ? { params: { partnerId } } : {}

        await httpClient.put(`/events/${eventId}`, eventData, config)
    } catch (error) {
        console.error(`Failed to update event with ID ${eventId}:`, error)
        throw new Error('Failed to update event')
    }
}

export const getEvents = async (searchTerm = '', isAdmin = false, partnerId?: string): Promise<EventType[]> => {
    try {
        const basePath = isAdmin ? '/events/admin' : '/events'
        const params: any = { search: searchTerm }

        if (isAdmin && partnerId) {
            params.partnerId = partnerId
        }

        const { data } = await httpClient.get<EventType[]>(basePath, { params })
        return data.map(processEventData)
    } catch (error) {
        console.error('Failed to fetch events:', error)
        throw new Error('Failed to fetch events')
    }
}

export const getAvailableEventLocations = async (eventId?: string | null, partnerId?: string): Promise<LocationType[]> => {
    try {
        const path = eventId ? `/events/locations/${eventId}` : `/events/locations/`
        const apiUrl = partnerId ? `${path}/organization/${partnerId}` : path

        const { data } = await httpClient.get<LocationType[]>(apiUrl)
        return data
    } catch (error) {
        console.error('Failed to fetch locations for events:', error)
        throw new Error('Failed to fetch locations for events')
    }
}

export const getAllAvailableEventLocations = async (): Promise<LocationType[]> => {
    try {
        const path = `/events/all-locations`

        const { data } = await httpClient.get<LocationType[]>(path)
        return data
    } catch (error) {
        console.error('Failed to fetch locations for events:', error)
        throw new Error('Failed to fetch locations for events')
    }
}

export const getEventByIdAdmin = async (eventId: string): Promise<EventDetails> => {
    try {
        const { data } = await httpClient.get<EventDetails>(`/events/admin/${eventId}`)
        return processEventData(data)
    } catch (error) {
        console.error(`Failed to fetch event with ID ${eventId}:`, error)
        throw new Error('Failed to fetch event')
    }
}

export const getEventById = async (eventId: string): Promise<EventDetails> => {
    try {
        const { data } = await httpClient.get<EventDetails>(`/events/${eventId}`)
        return processEventData(data)
    } catch (error) {
        console.error(`Failed to fetch event with ID ${eventId}:`, error)
        throw new Error('Failed to fetch event')
    }
}

export const getEventsByShowId = async (showId: string): Promise<EventDetails[]> => {
    try {
        const { data } = await httpClient.get<EventDetails[]>(`/events/show/${showId}`)
        return data.map(processEventData)
    } catch (error) {
        console.error(`Failed to fetch event with show ID ${showId}:`, error)
        throw new Error('Failed to fetch event with show ID')
    }
}

export const getEventsWithDetails = async (): Promise<EventDetails[]> => {
    try {
        const { data } = await httpClient.get<EventDetails[]>(`/events/details`)
        return data.map(processEventData)
    } catch (error) {
        console.error(`Failed to fetch events with current user`, error)
        throw new Error('Failed to fetch event with current user')
    }
}

export const deleteEvent = async (eventId: string): Promise<void> => {
    try {
        await httpClient.delete(`/events/${eventId}`)
    } catch (error) {
        console.error(`Failed to delete event with ID ${eventId}:`, error)
        throw new Error('Failed to delete event')
    }
}

const processEventData = (data: any) => {
    return {
        ...data,
        selling_start_date: formatDate(data.selling_start_date),
        selling_end_date: formatDate(data.selling_end_date),
        event_date: formatDate(data.event_date),
    }
}

export const createEventLocation = async (eventLocationData: any): Promise<any> => {
    try {
        const { data } = await httpClient.post('/event-locations', eventLocationData)
        return data
    } catch (error) {
        console.error('Failed to create locations for event:', error)
        throw new Error('Failed to create locations for event')
    }
}

export const updateEventLocation = async (eventLocationData: any): Promise<any> => {
    try {
        const { data } = await httpClient.put(`/event-locations/${eventLocationData.event_location_id}`, eventLocationData)
        return data
    } catch (error) {
        console.error('Failed to update locations for event:', error)
        throw new Error('Failed to update locations for event')
    }
}

export const getEventLocationDetails = async (id: string): Promise<LocationType> => {
    try {
        const { data } = await httpClient.get(`/event-locations/${id}`)
        return data.location
    } catch (error: any) {
        console.error('Error fetching location by ID:', error.response?.data || error.message)
        throw new Error('Failed to fetch location by ID')
    }
}
